import React, { useEffect, useState } from 'react';
import styles from './Sidebar.module.scss';
import { Button } from '@zurich-primitives/button';
import classnames from 'classnames';
import { ReactComponent as LightbulbIcon } from 'app/assets/Icons/Lightbulb.svg';
import { ReactComponent as LightningIcon } from 'app/assets/Icons/Chips/Lightning.svg';
import { ReactComponent as PlusIcon } from 'app/assets/Icons/Plus.svg';
import { createNewChat, getHistory } from 'integration';
import { ChatList } from './ItemsList/ChatList';
import { DocumentList } from './ItemsList/DocumentList';
import { IHistoryData, ISidebar } from './Sidebar.interface';
import { useAppSelector, getService, getBusinessArea } from 'app/redux';
import { SERVICE_NAMES } from 'app/utils/consts';
import { useNavigate } from 'react-router-dom';
import config from 'config';
import { LoadingSpinner } from 'app/components';
import { IChatTitles } from './ItemsList/ChatList.interface';

export const Sidebar: React.FC<ISidebar> = ({
  onChatSelect,
  selectedChatId,
  selectedModel,
  onModelChange,
  onNewChat,
  refreshChatList,
  onRefreshChatList
}) => {
  const currentService =
    useAppSelector(getService) ?? SERVICE_NAMES.ASK_YOUR_DOCS;
  const currentBusinessArea =
    useAppSelector(getBusinessArea) ?? 'cross-functional';
  const [historyData, setHistoryData] = useState<IHistoryData>();
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const navigate = useNavigate();
  const [chatHistory, setChatHistory] = useState<IChatTitles>({ chats: [] });

  const handleNewChat = (): void => {
    createNewChat(currentBusinessArea, currentService)
      .then((response) => {
        onNewChat(response.chatId);
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  };

  const handleGetHistory = (): void => {
    setIsHistoryLoading(true);
    getHistory(currentBusinessArea, currentService)
      .then((response: IHistoryData) => {
        setHistoryData(response);
        setChatHistory({ chats: response.chats });
      })
      .catch((error) => {
        console.error('Error fetching history:', error);
        navigate(config.APP.PATH.LANDING);
      })
      .finally(() => {
        setIsHistoryLoading(false);
      });
  };

  useEffect(() => {
    handleGetHistory();
  }, []);

  return (
    <div className={styles['side-bar']}>
      <span className={styles.header}>
        <h3 className={styles.title}>
          Ask Your Docs <span style={{ color: '#0F67BF' }}>Tool</span>
        </h3>
      </span>

      <span className={styles['model-toggle']}>
        <button
          className={classnames(styles['fast-model'], {
            [styles.active]: selectedModel === 'fast'
          })}
          onClick={() => onModelChange('fast')}
        >
          <LightningIcon />
          Faster Model
        </button>
        <button
          className={classnames(styles['smart-model'], {
            [styles.active]: selectedModel === 'smart'
          })}
          onClick={() => onModelChange('smart')}
        >
          <LightbulbIcon />
          Smarter Model
        </button>
      </span>

      <Button
        aria-label="Create Chat"
        variant="tertiary"
        size="small"
        className={styles['new-chat']}
        onClick={() => handleNewChat()}
      >
        <Button.Icon>
          <PlusIcon />
        </Button.Icon>
        <Button.Label>New Chat</Button.Label>
      </Button>

      <ChatList
        onChatSelect={onChatSelect}
        selectedChatId={selectedChatId}
        refreshChatList={refreshChatList}
        chatData={chatHistory}
        setChatData={setChatHistory}
      />

      <DocumentList
        documentData={{ documents: historyData?.documents ?? {} }}
        onRefreshChatList={onRefreshChatList}
      />
      {isHistoryLoading && <LoadingSpinner />}
    </div>
  );
};
